import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import { DateTime } from "luxon";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import LeaveApi from "../../../axios/LeaveAndHoliday/LeaveApi";
import { useAuth } from "../../../context/AuthContext";
import { useLoading } from "../../../context/LoadingContext";

ChartJS.register(ArcElement, Tooltip, Legend);

const createCenterTextPlugin = (pluginId) => ({
  id: pluginId,
  beforeDraw(chart) {
    const { width, height, ctx } = chart;
    const pluginOptions = chart?.config?.options?.plugins?.[pluginId] ?? {};
    const total = pluginOptions.total ?? 0;
    const title = pluginOptions.title ?? "";

    ctx.restore();

    const titleFont = Math.round(width / 18);
    const valueFont = Math.round(width / 8);

    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const maxWidth = width * 0.35;
    const words = title.split(" ");
    const lines = [];
    let line = "";

    ctx.font = `${titleFont}px sans-serif`;

    words.forEach((word) => {
      const testLine = line ? `${line} ${word}` : word;
      if (ctx.measureText(testLine).width > maxWidth) {
        lines.push(line);
        line = word;
      } else {
        line = testLine;
      }
    });

    if (line) lines.push(line);

    const lineHeight = titleFont + 2;
    const startY = height / 2 - (lines.length * lineHeight) / 2 - valueFont / 2;

    ctx.fillStyle = "#1F2937";
    lines.forEach((text, index) => {
      ctx.fillText(text, width / 2, startY + index * lineHeight);
    });

    ctx.font = `600 ${valueFont}px sans-serif`;
    ctx.fillText(String(total), width / 2, startY + lines.length * lineHeight + valueFont);
    ctx.save();
  },
});

const buildFromToByDateWithTZ = (selectedDate, timeZone) => {
  const zone = timeZone || "+00:00";
  const base = DateTime.fromJSDate(
    selectedDate instanceof Date ? selectedDate : new Date(selectedDate),
    { zone }
  );

  return {
    from: base.startOf("day").toUTC().toISO(),
    to: base.endOf("day").toUTC().toISO(),
  };
};

const LeaveAndHolidayChart = ({
  chartType,
  selectedDate,
  formatLeaveFilter = {},
  refreshData,
}) => {
  const { t } = useTranslation();
  const [show, hide] = useLoading();
  const { chartLeave, chartLeaveRequest } = LeaveApi();
  const { genaralSettingTimeZone, isAdmin, profile } = useAuth();

  const [chartCounts, setChartCounts] = useState({
    segment1: 0,
    segment2: 0,
    segment3: 0,
    total: 0,
  });
  const chartContainerRef = useRef(null);
  const [chartContainerWidth, setChartContainerWidth] = useState(0);

  useEffect(() => {
    const node = chartContainerRef.current;
    if (!node) return;

    const updateSize = () => {
      setChartContainerWidth(Math.round(node.getBoundingClientRect().width));
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(node);
    window.addEventListener("resize", updateSize);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateSize);
    };
  }, []);

  const centerPluginId = `leaveHolidayChartCenter_${chartType}`;
  const centerTextPlugin = useMemo(
    () => createCenterTextPlugin(centerPluginId),
    [centerPluginId]
  );

  useEffect(() => {
    let cancelled = false;

    const fetchChartData = async () => {
      if (!selectedDate) return;

      setChartCounts({
        segment1: 0,
        segment2: 0,
        segment3: 0,
        total: 0,
      });

      try {
        show();
        const timeZone = genaralSettingTimeZone || "+00:00";
        const { from, to } = buildFromToByDateWithTZ(selectedDate, timeZone);
        const langId = localStorage.getItem("i18nextLngId");

        // groupId / userId không bắt buộc — chỉ gửi khi có giá trị hợp lệ
        const groupId =
          formatLeaveFilter.group && formatLeaveFilter.group !== 1
            ? formatLeaveFilter.group
            : null;
        const userId = isAdmin
          ? formatLeaveFilter.userId?.id ?? null
          : profile?.userId ?? null;

        if (chartType === "leaves") {
          const payload = {
            from,
            to,
            langId,
          };
          if (userId) payload.userId = userId;
          if (groupId) payload.groupId = groupId;

          const res = await chartLeave(payload);
          if (cancelled) return;

          const chartItem = Array.isArray(res?.data?.data)
            ? res.data.data[0]
            : res?.data?.data;
          const onLeave = Number(chartItem?.onLeave) || 0;
          const totalUser = Number(chartItem?.totalUser) || 0;

          setChartCounts({
            segment1: onLeave,
            segment2: Math.max(totalUser - onLeave, 0),
            segment3: 0,
            // Số lớn ở giữa chart = onLeave
            total: onLeave,
          });
          return;
        }

        const statusFilter =
          formatLeaveFilter.selecStatusLeave !== 0
            ? formatLeaveFilter.selecStatusLeave
            : null;

        const requestStatusMap = {
          1: "APPROVED",
          2: "REJECTED",
          3: "PENDING",
        };

        const requestPayload = {
          from,
          to,
          requestStatus: statusFilter ? requestStatusMap[statusFilter] : null,
          langId,
        };
        if (userId) requestPayload.userId = userId;
        if (groupId) requestPayload.groupId = groupId;

        const { data } = await chartLeaveRequest(requestPayload);

        if (cancelled) return;

        const chartData = data?.data;
        const chartItem = Array.isArray(chartData)
          ? chartData[0]
          : chartData;

        const getCountFromObject = (keys) => {
          for (const key of keys) {
            const value = chartItem?.[key];
            if (value !== undefined && value !== null) {
              return Number(value) || 0;
            }
          }
          return 0;
        };

        const getCountFromArray = (status) => {
          if (!Array.isArray(chartData)) return 0;

          return chartData.reduce((sum, item) => {
            const currentStatus = String(
              item?.status ||
                item?.requestStatus ||
                item?.requestStatusName ||
                item?.name ||
                ""
            ).toUpperCase();

            if (currentStatus !== status) return sum;

            return (
              sum +
              (Number(
                item?.count ??
                  item?.total ??
                  item?.value ??
                  item?.totalRequest
              ) || 0)
            );
          }, 0);
        };

        const approved = getCountFromObject([
          "approved",
          "approvedCount",
          "approve",
          "approveCount",
          "APPROVED",
        ]) || getCountFromArray("APPROVED");
        const rejected = getCountFromObject([
          "rejected",
          "rejectedCount",
          "reject",
          "rejectCount",
          "REJECTED",
        ]) || getCountFromArray("REJECTED");
        const pending = getCountFromObject([
          "pending",
          "pendingCount",
          "requested",
          "requestedCount",
          "PENDING",
        ]) || getCountFromArray("PENDING");
        const total =
          getCountFromObject(["total", "totalRequest", "totalRequests", "totalElements"]) ||
          approved + rejected + pending;

        setChartCounts({
          segment1: approved,
          segment2: rejected,
          segment3: pending,
          total,
        });
      } catch (error) {
        if (cancelled) return;
        console.error(error);
        setChartCounts({
          segment1: 0,
          segment2: 0,
          segment3: 0,
          total: 0,
        });
      } finally {
        hide();
      }
    };

    fetchChartData();

    return () => {
      cancelled = true;
    };
  }, [
    chartType,
    selectedDate,
    formatLeaveFilter?.group,
    formatLeaveFilter?.userId?.id,
    formatLeaveFilter?.selecStatusLeave,
    refreshData,
    genaralSettingTimeZone,
    isAdmin,
    profile?.userId,
  ]);

  const chartConfig = useMemo(() => {
    if (chartType === "leaves") {
      return {
        labels: [t("common.on-leave"), t("common.employees")],
        colors: ["#FF0066", "#F3F4F6"],
        title: t("common.on-leave"),
        emptyText: t("common.no-data"),
        legendLabels: [t("common.leaves")],
        legendColors: ["#FF0066"],
      };
    }

    return {
      labels: [t("common.approved"), t("common.rejected"), t("common.pending")],
      colors: ["#058900", "#D00D00", "#F97C00"],
      title: t("common.total-request"),
      emptyText: t("common.no-data"),
      legendLabels: [
        t("common.approved"),
        t("common.rejected"),
        t("common.pending"),
      ],
      legendColors: ["#058900", "#D00D00", "#F97C00"],
    };
  }, [chartType, t]);

  const { segment1, segment2, segment3, total } = chartCounts;

  const chartValues =
    chartType === "leaves"
      ? [segment1, segment2]
      : [segment1, segment2, segment3];

  const activeSegments = chartValues.filter((item) => item > 0).length;
  const hasMultipleSegments = activeSegments > 1;
  // leaves: có data khi totalUser > 0 (segment1+segment2) hoặc onLeave > 0
  const hasChartData =
    chartType === "leaves"
      ? segment1 > 0 || segment2 > 0
      : total > 0;

  const chartData = useMemo(
    () => ({
      labels: chartConfig.labels,
      datasets: [
        {
          data: chartValues,
          backgroundColor: chartConfig.colors,
          borderWidth: 0,
          spacing: hasMultipleSegments ? 2 : 0,
          borderRadius: hasMultipleSegments ? 8 : 0,
        },
      ],
    }),
    [chartValues, chartConfig, hasMultipleSegments]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "68%",
      plugins: {
        legend: { display: false },
        [centerPluginId]: {
          title: chartConfig.title,
          total,
        },
      },
    }),
    [centerPluginId, chartConfig.title, total]
  );

  const legendData =
    chartType === "leaves"
      ? [{ color: chartConfig.legendColors[0], display: chartConfig.legendLabels[0] }]
      : chartConfig.legendLabels.map((label, index) => ({
          color: chartConfig.legendColors[index],
          display: label,
          value: chartValues[index],
        }));

  return (
    <div className="px-2 sm:px-4 py-3 sm:py-4 h-full w-full min-w-0 flex flex-col justify-between gap-3">
      <div className="flex items-center justify-center flex-1 min-h-0 w-full">
        <div
          ref={chartContainerRef}
          className="aspect-square w-full max-w-full mx-auto"
        >
          {hasChartData ? (
            <Doughnut
              key={`${chartType}-${chartContainerWidth}`}
              data={chartData}
              options={options}
              plugins={[centerTextPlugin]}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <p className="text-center text-gray-400 text-xs sm:text-sm px-3 sm:px-5">
                {chartConfig.emptyText}
              </p>
            </div>
          )}
        </div>
      </div>

      {hasChartData && (
        <div className="flex flex-wrap items-center justify-center gap-x-5 sm:gap-x-8 gap-y-2 sm:gap-y-3 px-1 sm:px-2 w-full">
          {legendData.map((item, index) => (
            <div key={index} className="flex items-center justify-center gap-1.5 py-0.5 sm:py-1 max-w-full">
              <div
                className="rounded-full w-2.5 h-2.5 sm:w-3 sm:h-3 shrink-0"
                style={{ backgroundColor: item.color }}
              />
              <p className="text-[10px] sm:text-xs text-[#262626] truncate">
                {item.display}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeaveAndHolidayChart;
